import { useTranslation } from '@/hooks/use-translation';
import { motion } from 'motion/react';

export function Hero() {
  const { t } = useTranslation();

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-slate-50 dark:bg-[#0a0f14]">
      {/* Grid background */}
      <div className="absolute inset-0 opacity-20 dark:opacity-20">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern
              id="grid"
              width="60"
              height="60"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 60 0 L 0 0 0 60"
                fill="none"
                className="stroke-slate-300 dark:stroke-[#3d5a6e]"
                strokeWidth="0.5"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>

      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-linear-to-b from-transparent via-slate-50/50 dark:via-[#0a0f14]/50 to-slate-50 dark:to-[#0a0f14]" />

      {/* Floating data points */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(12)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 rounded-full bg-[#5a8a9a]"
            style={{
              left: `${10 + i * 7}%`,
              top: `${20 + (i % 4) * 15}%`,
            }}
            animate={{
              opacity: [0.2, 0.6, 0.2],
              scale: [1, 1.5, 1],
            }}
            transition={{
              duration: 3 + (i % 3),
              repeat: Infinity,
              delay: i * 0.3,
            }}
          />
        ))}
      </div>

      {/* Isometric city lines */}
      <div className="absolute bottom-0 left-0 right-0 h-[40%] opacity-20 dark:opacity-30">
        <svg
          viewBox="0 0 1200 400"
          className="w-full h-full"
          preserveAspectRatio="xMidYMax slice"
        >
          {/* Building outlines */}
          <g
            className="stroke-slate-400 dark:stroke-[#5a8a9a]"
            strokeWidth="1"
            fill="none"
          >
            <path d="M100,400 L100,300 L200,250 L200,400" />
            <path d="M200,400 L200,250 L300,200 L300,400" />
            <path d="M300,400 L300,200 L350,180 L350,400" />
            <path d="M400,400 L400,280 L500,230 L500,400" />
            <path d="M500,400 L500,230 L600,180 L600,400" />
            <path d="M600,400 L600,180 L650,160 L650,400" />
            <path d="M700,400 L700,260 L800,210 L800,400" />
            <path d="M800,400 L800,210 L900,160 L900,400" />
            <path d="M900,400 L900,160 L950,140 L950,400" />
            <path d="M1000,400 L1000,300 L1100,250 L1100,400" />
          </g>
          {/* Data overlay lines */}
          <g
            className="stroke-[#5a8a9a] dark:stroke-[#7ab8c9]"
            strokeWidth="0.5"
            strokeDasharray="4,4"
            opacity="0.5"
          >
            <line x1="0" y1="300" x2="1200" y2="300" />
            <line x1="0" y1="250" x2="1200" y2="250" />
            <line x1="0" y1="200" x2="1200" y2="200" />
          </g>
        </svg>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <span className="inline-block mb-6 text-[#3d6a7a] dark:text-[#7ab8c9] text-sm md:text-base tracking-[0.25em] uppercase font-medium">
            {t('hero.tagline')}
          </span>
        </motion.div>

        <motion.h1
          className="font-display text-slate-900 dark:text-[#e8ebe9] text-5xl md:text-6xl lg:text-7xl xl:text-8xl leading-[1.05] mb-10 tracking-tight"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          {t('hero.title')}
          <br />
          <span className="text-[#3d6a7a] dark:text-[#7ab8c9]">
            {t('hero.titleHighlight')}
          </span>
        </motion.h1>

        <motion.p
          className="text-slate-600 dark:text-[#8a9a9e] text-lg md:text-xl lg:text-2xl max-w-3xl mx-auto mb-14 leading-relaxed font-light"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          {t('hero.description')}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <a
            href="#solution"
            className="inline-flex items-center gap-3 px-10 py-4 rounded-xl border border-slate-300 dark:border-[#3d5a6e] text-slate-800 dark:text-[#e8ebe9] text-sm md:text-base font-medium tracking-wide hover:bg-slate-100 dark:hover:bg-[#1a2530] hover:border-slate-400 dark:hover:border-[#5a8a9a] transition-all duration-300 ease-out hover:scale-[1.02] active:scale-[0.98] hover:shadow-lg"
          >
            {t('hero.cta')}
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M19 14l-7 7m0 0l-7-7m7 7V3"
              />
            </svg>
          </a>
        </motion.div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-linear-to-t from-slate-100 dark:from-[#0d1318] to-transparent" />
    </section>
  );
}
